import { useRef, useState } from 'react'
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AutoComplete, type AutoCompleteCompleteEvent } from 'primereact/autocomplete'
import FormField from '../FormField'
import { resolveFieldError } from '../../../lib/forms/messages'
import { matchSlots, normalizeTime } from './timeSlots'

/** Panel açılınca listeyi alandaki saate kaydırır. */
function scrollToCurrent(panel: HTMLElement | null | undefined): void {
  const list = panel?.querySelector<HTMLElement>('.p-autocomplete-items-wrapper')
  const current = panel?.querySelector<HTMLElement>('.ui-time-slot.is-current')?.closest('li')
  if (list && current) list.scrollTop = current.offsetTop - list.offsetTop
}

interface FormTimeSelectFieldProps<T extends FieldValues> {
  control: Control<T>
  name: FieldPath<T>
  label: string
  required?: boolean
  full?: boolean
  /** Etiketi görsel olarak gizler (bkz. `FormField.hideLabel`). */
  hideLabel?: boolean
  /** Kullanıcı değeri değiştirdiğinde çağrılır (form `reset`'inde çağrılmaz). */
  onValueChange?: (value: string) => void
}

/**
 * RHF-bağlı seçilebilir + yazılabilir saat: 30 dk adımlı liste, elle yazım serbest; değer "HH:mm".
 * `Dropdown editable` değil: PrimeReact onda `inputId`'yi gizli öğeye verir, `<label htmlFor>` görünen
 * kutuya bağlanmaz. Kutudan çıkınca kısa yazımlar tamamlanır ({@link normalizeTime}).
 */
export default function FormTimeSelectField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  full,
  hideLabel,
  onValueChange,
}: Readonly<FormTimeSelectFieldProps<T>>) {
  const { t } = useTranslation()
  const acRef = useRef<AutoComplete<string>>(null)
  // Her aramada YENİ dizi: PrimeReact paneli yalnız `suggestions` referansı değişince açar.
  const [suggestions, setSuggestions] = useState<string[]>([])
  const complete = (e: AutoCompleteCompleteEvent) => setSuggestions(matchSlots(e.query))

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const current = typeof field.value === 'string' ? field.value : ''
        const commit = (next: string) => {
          if (next === current) return
          field.onChange(next)
          onValueChange?.(next)
        }
        return (
          <FormField
            label={label}
            htmlFor={name}
            required={required}
            full={full}
            hideLabel={hideLabel}
            error={resolveFieldError(fieldState.error?.message, t)}
          >
            <AutoComplete<string>
              ref={acRef}
              inputId={name}
              value={current}
              suggestions={suggestions}
              completeMethod={complete}
              dropdown
              dropdownAriaLabel={t('common.timeOptions', { label })}
              maxLength={5}
              onChange={(e) => commit(typeof e.value === 'string' ? e.value : '')}
              onBlur={() => {
                commit(normalizeTime(current))
                field.onBlur()
              }}
              onShow={() => scrollToCurrent(acRef.current?.getOverlay())}
              itemTemplate={(slot: string) => (
                <span className={slot === current ? 'ui-time-slot is-current' : 'ui-time-slot'}>
                  {slot}
                </span>
              )}
              className={fieldState.error ? 'ui-time-select p-invalid' : 'ui-time-select'}
              pt={{
                input: {
                  root: {
                    'aria-invalid': fieldState.error ? 'true' : 'false',
                    'aria-describedby': `${name}-err`,
                  },
                },
              }}
            />
          </FormField>
        )
      }}
    />
  )
}
